import React, { memo, useMemo } from 'react'
import { Card, Spin } from 'antd'
import ReactEcharts from 'echarts-for-react'
import './SuperadminUsageByService.scss'

const fmt = (num, decimals = 2) => {
  if (num === null || num === undefined) return '—'
  const n = parseFloat(num)
  if (isNaN(n)) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
}

const fmtCost = (num) => {
  if (!num) return '$0.00'
  return `$${parseFloat(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
}

const SuperadminUsageByService = memo(({ data, loading }) => {
  const services = useMemo(() => {
    if (!Array.isArray(data?.serviceUsage)) return []
    return data.serviceUsage
  }, [data])

  const visibleServices = useMemo(() => services.filter(s => s.credits > 0), [services])

  const chartOption = useMemo(() => {
    if (visibleServices.length === 0) return null
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const s = visibleServices.find(x => x.displayName === params.name)
          return `<b>${params.name}</b><br/>Credits: ${fmt(params.value)}<br/>Cost: ${fmtCost(s?.cost)}<br/>${params.percent?.toFixed(1)}%`
        }
      },
      legend: {
        show: false
      },
      series: [
        {
          name: 'Usage by Service',
          type: 'pie',
          radius: ['52%', '76%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            scale: true,
            label: { show: false }
          },
          data: visibleServices.map(s => ({
            value: s.credits,
            name: s.displayName,
            itemStyle: { color: s.color }
          }))
        }
      ]
    }
  }, [visibleServices])

  if (!data && !loading) return null

  return (
    <Card className="superadmin-usage-service-card">
      <Spin spinning={loading}>
        <div className="sus-inner">
          <h3 className="sus-title">Cost by Service</h3>
          <div className="sus-chart-table">
            <div className="sus-chart">
              {chartOption ? (
                <ReactEcharts
                  option={chartOption}
                  notMerge={true}
                  className="sus-echarts"
                  opts={{ renderer: 'svg' }}
                  style={{ height: '220px' }}
                />
              ) : (
                <div className="sus-no-data">No usage data available</div>
              )}
            </div>
            <div className="sus-table-wrap">
              <table className="sus-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Credits</th>
                    <th>Cost ($)</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s, i) => (
                    <tr key={i} className={s.credits === 0 ? 'sus-row-zero' : ''}>
                      <td>
                        <div className="sus-service-name">
                          <span className="sus-dot" style={{ backgroundColor: s.color }} />
                          {s.displayName}
                        </div>
                      </td>
                      <td>{fmt(s.credits)}</td>
                      <td className="sus-cost-cell">{fmtCost(s.cost)}</td>
                      <td>{typeof s.percent === 'number' ? `${s.percent.toFixed(1)}%` : '0%'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Spin>
    </Card>
  )
})

SuperadminUsageByService.displayName = 'SuperadminUsageByService'
export default SuperadminUsageByService
